<template>

  <shell :title="$t('titles.blackouts')">

    <table-tools :actions="tableActions" :headers="headers" v-model:enabled-headers="enabledHeaders" remember-headers="blackouts-index-new">

      <table-filter v-if="canSeeIds">
        <o-text-field
          :label="$t('titles.id')"
          v-model="filters.ID"
          clearable
        />
      </table-filter>

      <table-filter v-if="canManageOrganizations">
        <o-autocomplete
          :label="$t('titles.organization')"
          v-model.number="filters.organizationID"
          :items="Organization.getOptions()"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-text-field
          :label="$t('titles.name')"
          v-model="filters.name"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-autocomplete
          :label="$t('titles.location')"
          v-model.number="filters.locationID"
          :items="locationOptions"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-select
          :label="$t('titles.status')"
          v-model="filters.status"
          :items="[
            {value: 'current', title: $t('enums.blackout_meta_statuses.current')},
            {value: 'expired', title: $t('enums.blackout_meta_statuses.expired')},
            {value: 'deleted', title: $t('enums.blackout_meta_statuses.deleted')},
          ]"
          clearable
        />
      </table-filter>

    </table-tools>

    <v-data-table
      ref="dataTable"
      :headers="visibleHeaders"
      :items="blackouts"
      :sort-by="sortBy"
      v-model:page="page"
      :items-per-page="itemsPerPage"
      :loading="loadingBlackouts"
    >

      <template v-slot:item.location.locationName="{ item }">
        <router-link :to="{ name: 'locationDetail', params: { locationID: item.locationID } }">
          {{ item.location.locationName }}
        </router-link>
      </template>

      <template v-slot:item.startDate="{ item }">
        {{ Temporal.PlainDateTime.from(item.startDate).toLocaleString('en', {year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit'}) }}
      </template>

      <template v-slot:item.endDate="{ item }">
        {{ Temporal.PlainDateTime.from(item.endDate).toLocaleString('en', {year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit'}) }}
      </template>

      <template v-slot:item.channels="{ item }">
        <template v-if="!item.channels">All</template>
        <template v-if="item.channels">
          <template v-for="(channel, i) in item.channels">
            <template v-if="i > 0">, </template>
            {{ ChannelRegistry.find(channel)?.titleShort ?? channel }}
          </template>
        </template>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip label :color="getDisplayStatus(item).color">
          {{ getDisplayStatus(item).title }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">

        <!--
        I tried implementing this as a single ActionGroup that used a
        getActions() function to populate the actions prop - but that resulted
        in very weird behavior when paging and sorting. (Specifically, the $edit
        icon would sometimes appear as a $view icon.) Couldn't figure it out,
        but this gets the job done.
        -->
        
        <ActionGroup
          :actions="[
            {label: t('actions.view_history'), icon: '$history', event: 'auditLogClicked'},
            {label: t('actions.edit'), icon: '$edit', event: 'editClicked', disabled: !writableLocationIDs.includes(item.locationID)},
            {label: t('actions.delete'), icon: '$delete', event: 'deleteClicked', disabled: !writableLocationIDs.includes(item.locationID)},
            {label: t('actions.view'), icon: '$view', event: 'editClicked', disabled: writableLocationIDs.includes(item.locationID)},
          ]"
          @edit-clicked="editBlackout(item)"
          @delete-clicked="deleteBlackout(item)"
          @audit-log-clicked="showAuditLog(item)"
        />

      </template>

      <template v-slot:bottom>
        <data-table-pagination
          :items="blackouts"
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          @update:page="dataTable?.$el.scrollIntoView()"
        />
      </template>

    </v-data-table>

  </shell>

</template>

<script setup lang="ts">

import { computed, inject, reactive, ref, useTemplateRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

// components
import BlackoutCreateModal from './BlackoutCreateModal.vue'
import BlackoutEditModal from './BlackoutEditModal.vue'
import BlackoutDeleteModal from './BlackoutDeleteModal.vue'
// @ts-ignore: don't complain that BlackoutAuditLogModal isn't TypeScript
import BlackoutAuditLogModal from './BlackoutAuditLogModal.vue'
import DataTablePagination from '../../DataTablePagination.vue'
import Shell from '../Shell.vue'

// models
import Blackout from '../../../models/Blackout'
import Location from '../../../models/Location'
import Organization from '../../../models/Organization'

// misc
// @ts-ignore: don't complain about missing types
import { openDialog } from 'vue3-promise-dialog'
import ChannelRegistry from '../../../channels/ChannelRegistry'
import NotificationService from '../../../services/NotificationService'
import { QuerySyncer, AbstractSerializer, NumberSerializer } from '../../../utils/QuerySyncer'
import type { Can } from '../../../main'
import type { VAutocompleteItem, VSortItem } from '../../../types/vuetify'
import type { VDataTable } from 'vuetify/components'
import { Temporal } from '@js-temporal/polyfill'

const route = useRoute()
const { t } = useI18n()
const title = useTitle()
title.value = t('titles.blackouts')

const $can = inject('$can') as Can
const canSeeIds = $can('seeids')
const canManageOrganizations = $can('organizations.manage')

// so that we don't have to call $can('blackouts.*') constantly, precompute
const readableLocationIDs: number[] = []
const writableLocationIDs: number[] = []

Location.all().forEach((location) => {
  if ($can('blackouts.write', location.ID)) {
    writableLocationIDs.push(location.ID)
    readableLocationIDs.push(location.ID)
  } else if ($can('blackouts.read', location.ID)) {
    readableLocationIDs.push(location.ID)
  }
})

interface Filters {
  ID: null | string
  organizationID: null | number
  name: string
  locationID: null | number
  status: null | 'current' | 'expired' | 'deleted'
}

const filters = reactive<Filters>({
  ID: null,
  organizationID: null,
  name: '',
  locationID: null,
  status: 'status' in route.query ? null : 'current', // kludge. I think maybe QuerySyncer should be a composable instead of the weird thing that it currently is
})

class StatusSerializer extends AbstractSerializer {

  static serialize(value: any) {
    return value === null ? 'any' : value
  }

  static unserialize(value: any) {
    return value === 'any' ? null : value
  }

}

new QuerySyncer({
  id: {
    watchable: () => filters.ID,
    hydrate: (newValue) => filters.ID = newValue,
    serializer: NumberSerializer,
  },
  organization_id: {
    watchable: () => filters.organizationID,
    hydrate: (newValue) => filters.organizationID = newValue,
    serializer: NumberSerializer,
  },
  name: {
    watchable: () => filters.name,
    hydrate: (newValue) => filters.name = newValue,
  },
  location_id: {
    watchable: () => filters.locationID,
    hydrate: (newValue) => filters.locationID = newValue,
    serializer: NumberSerializer,
  },
  status: {
    watchable: () => filters.status,
    hydrate: (newValue) => filters.status = newValue,
    serializer: StatusSerializer,
  },
})

const tableActions = computed(() => [
  {
    title: t('actions.reload'),
    icon: '$reload',
    click: loadBlackouts,
    busy: loadingBlackouts.value,
  },
  {
    title: t('actions.create_blackout'),
    icon: '$create',
    click: createBlackout,
    hidden: writableLocationIDs.length === 0,
  },
])

/**
 * Pagination
 */

const page = ref(1)
const itemsPerPage = ref(10)

// when anything that affects the main query is changed, reset the page to 1
watch(filters, () => page.value = 1)

// When the "Organization" dropdown is changed, check to see if the selected
// location belongs to a different org. If so, deselect it. (Otherwise, it
// will look like no location is selected - but filters.locationID will still
// have a value.)
watch(() => filters.organizationID, (newOrganizationID) => {
  const isOrganizationSelected = newOrganizationID || newOrganizationID === 0
  if (isOrganizationSelected && filters.locationID) {
    const location = Location.find(filters.locationID)
    if (!location || location.organizationID !== newOrganizationID) {
      filters.locationID = null
    }
  }
})

const locationOptions = computed<VAutocompleteItem[]>(() => {

  let selectedOrganizationID = null

  // filters.organizationID will sometimes be 0 (if Ocra is selected), so we
  // have to be careful here...
  if (filters.organizationID || filters.organizationID === 0) {
    selectedOrganizationID = filters.organizationID
  }

  return Location.getOptions('blackouts', 'read', selectedOrganizationID)

})

/**
 * Data table configs
 */

const dataTable = useTemplateRef<VDataTable>('dataTable')

const headers = [
  {title: t('titles.id'), key: 'ID', width: 100, hidden: !$can('seeids')},
  {title: t('titles.name'), key: 'name', toggleable: false},
  {title: t('titles.location'), key: 'location.locationName', width: 240},
  {title: t('titles.start'), key: 'startDate', width: 120},
  {title: t('titles.end'), key: 'endDate', width: 120},
  {title: t('titles.channels'), key: 'channels'},
  {title: t('titles.status'), key: 'status', width: 100},
  {title: '', key: 'actions', sortable: false, width: 100, hideFromPicker: true},
]

const enabledHeaders = ref([
  'ID', 'name', 'location.locationName', 'startDate', 'endDate',
  'channels', 'status', 'actions',
])

const visibleHeaders = computed(() => {
  return headers.filter((header) => {
    return !header.hidden && enabledHeaders.value.includes(header.key)
  })
})

const sortBy = ref<VSortItem[]>([{ key: 'startDate', order: 'asc' }])

/**
 * Blackouts data
 */

const loadingBlackouts = ref<boolean>(false)

function loadBlackouts() {

  loadingBlackouts.value = true

  Blackout.fetchAll()
    .catch((error) => {
      console.error(error)
      NotificationService.error('Couldn\'t load blackouts!')
    })
    .finally(() => {
      loadingBlackouts.value = false
    })

}

const deletedStatuses = ['pending delete', 'deleted', 'failed delete']

const blackouts = computed<Blackout[]>(() => {

  const matchingBlackouts: Blackout[] = []

  const query = Blackout.query()
    .with('location')
    .with('location.organization')

  if (filters.name && filters.name.length > 0) {
    query.where((blackout: Blackout) => {
      return blackout.name.toUpperCase().indexOf(
        filters.name.trim().toUpperCase()
      ) !== -1
    })
  }

  if (filters.locationID) {
    query.where('locationID', filters.locationID)
  }
  
  const blackouts = query.get()

  blackouts.forEach((blackout) => {

    if (filters.ID && !blackout.ID.toString().includes(filters.ID)) {
      return
    }

    if (filters.organizationID !== null) {
      if (
        typeof blackout.location === undefined ||
        blackout.location.organizationID !== filters.organizationID
      ) {
        return
      }
    }

    if (!readableLocationIDs.includes(blackout.locationID)) {
      return
    }

    let blackoutMatches = false

    if (!filters.status) {

      blackoutMatches = true

    } else {

      const now = Temporal.Now.zonedDateTimeISO(blackout.location.timezone).toPlainDateTime()
      const blackoutEnd = Temporal.PlainDateTime.from(blackout.endDate)

      switch (filters.status) {
        case 'current':
          blackoutMatches = !deletedStatuses.includes(blackout.status) && Temporal.PlainDateTime.compare(blackoutEnd, now) > 0
          break
        case 'expired':
          blackoutMatches = !deletedStatuses.includes(blackout.status) && Temporal.PlainDateTime.compare(blackoutEnd, now) === -1
          break
        case 'deleted':
          blackoutMatches = deletedStatuses.includes(blackout.status)
          break
      }

    }

    if (blackoutMatches) {
      matchingBlackouts.push(blackout)
    }

  })

  return matchingBlackouts

})

/**
 * Modals
 */

function createBlackout() {
  openDialog(BlackoutCreateModal)
}

function editBlackout(blackout: Blackout) {
  openDialog(BlackoutEditModal, { blackout })
}

function deleteBlackout(blackout: Blackout) {
  openDialog(BlackoutDeleteModal, { blackout })
}

function showAuditLog(blackout: Blackout) {
  openDialog(BlackoutAuditLogModal, { blackout })
}

/**
 * Utility functions
 */

function getDisplayStatus(blackout: Blackout) {

  if (deletedStatuses.includes(blackout.status)) {
    return { title: 'Deleted', color: 'error' }
  }

  const now = Temporal.Now.zonedDateTimeISO(blackout.location.timezone).toPlainDateTime()
  const blackoutEnd = Temporal.PlainDateTime.from(blackout.endDate)

  if (Temporal.PlainDateTime.compare(blackoutEnd, now) > 0) {
    return { title: 'Current', color: 'success' }
  } else {
    return { title: 'Expired', color: '' }
  }

}

</script>
